import { useState, useCallback } from 'react'
import { Eye, EyeOff, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { validateMnemonic, getWordlist } from '../../lib/wallet'
import { truncateAddress } from '../../lib/utils'

const wordlist = getWordlist()

export default function SeedPhraseLogin() {
  const { login } = useAuth()
  const [words, setWords] = useState<string[]>(Array(12).fill(''))
  const [showPhrase, setShowPhrase] = useState(false)
  const [previewAddress, setPreviewAddress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleWordChange = useCallback((index: number, value: string) => {
    const newWords = [...words]
    newWords[index] = value.toLowerCase().trim()
    setWords(newWords)
    setPreviewAddress(null)
    setError(null)
  }, [words])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const pastedWords = pastedText.toLowerCase().trim().split(/\s+/)

    if (pastedWords.length === 12 || pastedWords.length === 24) {
      const newWords = pastedWords.length === 12
        ? pastedWords
        : pastedWords.slice(0, 12)
      setWords(newWords.map(w => w.trim()))
      setError(null)
    }
  }, [])

  const toggleWordCount = useCallback(() => {
    if (words.length === 12) {
      setWords([...words, ...Array(12).fill('')])
    } else {
      setWords(words.slice(0, 12))
    }
    setPreviewAddress(null)
    setError(null)
  }, [words])

  const mnemonic = words.join(' ').trim()
  const isValid = validateMnemonic(mnemonic)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isValid) {
      setError('Invalid seed phrase. Please check your words.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await login(mnemonic)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
      setLoading(false)
    }
  }

  const getWordValidation = (word: string): 'valid' | 'invalid' | 'empty' => {
    if (!word) return 'empty'
    return wordlist.includes(word) ? 'valid' : 'invalid'
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Word count toggle */}
      <div className="flex justify-center gap-2">
        <button
          type="button"
          onClick={() => words.length !== 12 && toggleWordCount()}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            words.length === 12
              ? 'bg-doge-500 text-slate-900'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          12 Words
        </button>
        <button
          type="button"
          onClick={() => words.length !== 24 && toggleWordCount()}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            words.length === 24
              ? 'bg-doge-500 text-slate-900'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          24 Words
        </button>
      </div>

      {/* Seed phrase grid */}
      <div
        className={`grid gap-2 sm:gap-3 ${words.length === 12 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'}`}
        onPaste={handlePaste}
      >
        {words.map((word, index) => {
          const validation = getWordValidation(word)
          return (
            <div key={index} className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                {index + 1}.
              </span>
              <input
                type={showPhrase ? 'text' : 'password'}
                value={word}
                onChange={(e) => handleWordChange(index, e.target.value)}
                className={`w-full pl-8 pr-3 py-2 bg-slate-800 rounded-lg border text-sm font-mono
                  ${validation === 'valid' ? 'border-green-500/50' : ''}
                  ${validation === 'invalid' ? 'border-red-500/50' : ''}
                  ${validation === 'empty' ? 'border-slate-600' : ''}
                  focus:outline-none focus:ring-2 focus:ring-doge-500 focus:border-transparent
                  text-white placeholder-slate-500`}
                placeholder="word"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )
        })}
      </div>

      {/* Show/Hide toggle */}
      <button
        type="button"
        onClick={() => setShowPhrase(!showPhrase)}
        className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mx-auto"
      >
        {showPhrase ? <EyeOff size={16} /> : <Eye size={16} />}
        <span className="text-sm">{showPhrase ? 'Hide' : 'Show'} seed phrase</span>
      </button>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
          <AlertCircle size={16} className="text-red-400" />
          <span className="text-sm text-red-400">{error}</span>
        </div>
      )}

      {/* Preview address */}
      {previewAddress && (
        <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/50 rounded-lg">
          <CheckCircle2 size={16} className="text-green-400" />
          <div className="flex-1">
            <p className="text-xs text-green-400">Wallet Address:</p>
            <p className="font-mono text-sm text-white">{truncateAddress(previewAddress, 10)}</p>
          </div>
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={!isValid || loading}
        className="w-full btn-primary py-3 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            Connecting...
          </>
        ) : (
          'Connect Wallet'
        )}
      </button>

      <p className="text-xs text-slate-500 text-center">
        Your wallet key is stored locally and never transmitted.
        <br />
        Make sure you are on a secure device.
      </p>
    </form>
  )
}
